let instances = [];

class Carousel {
    /**
     * Create a new carousel element.
     *
     * @param {HTMLElement} element
     */
    constructor(element) {
        this.element = element;
        this.id = element.getAttribute("data-id");
        this.autoPlay = !!element.getAttribute("data-auto-play");
        this.autoPlayDuration = element.getAttribute("data-auto-play-duration");
        this.wrapAround = !!element.getAttribute("data-wrap-around");
        this.multiSlide = !!element.getAttribute("data-multi-slide");
        this.centeredSlides = !!element.getAttribute("data-centered-slides");
        this.freeSwipe = !!element.getAttribute("data-free-swipe");
        this.loop = !!element.getAttribute("data-loop");
        this.cssMode = !element.getAttribute("data-disable-css-mode");
        this.thumbCarousel = element.getAttribute("data-thumb-carousel");
        this.touchStartPrevent = !!element.getAttribute("data-touch-start-prevent");
        this.initCarousel();
    }

    /**
     * Initialise a carousel.
     *
     * @return {void}
     */
    initCarousel() {
        /* pause videos on carousel change, if there are any */
        const videoWrapperElements = this.element.getElementsByClassName("video-wrapper");
        const videoIds = Array.from(videoWrapperElements).map((ele) => ele.id);

        let carouselOptions = {
            draggable: true,
            pageDots: true,
            touchStartPreventDefault: this.touchStartPrevent,
            // Note: there is a bug with using cssMode + slidesPerView which impacts the imageCarousel.
            // We are still using cssMode where possible due to the performance improvements.
            cssMode: this.cssMode,
            rewind: this.wrapAround,
            loop: this.loop,
            slidesPerGroup: 1,
            spaceBetween: this.multiSlide ? 8 : 0,
            watchOverflow: true,
            centeredSlides: this.centeredSlides,
            autoplay: {
                enabled: false,
            },
            freeMode: this.freeSwipe,
            on: {
                slideChange: () => {
                    videoIds.forEach((wrapperId) =>
                        window[wrapperId]?.pauseVideo ? window[wrapperId].pauseVideo() : null,
                    );
                },
                lock: (swiper) => {
                    swiper.wrapperEl.classList.add("swiper-locked");
                },
                unlock: (swiper) => {
                    swiper.wrapperEl.classList.remove("swiper-locked");
                },
                navigationNext: (swiper) => {
                    swiper.autoplay.stop();
                },
                navigationPrev: (swiper) => {
                    swiper.autoplay.stop();
                },
                touchStart: (swiper) => {
                    swiper.autoplay.stop();
                },
            },
        };

        var that = this;

        import("./libs").then(({ default: { Swiper, Navigation, Pagination, Autoplay, Thumbs } }) => {
            let thumbSwiper = null;
            if (that.thumbCarousel) {
                const thumbSwiperEl = document.querySelector(`.${that.thumbCarousel}`);
                thumbSwiper = new Swiper(thumbSwiperEl, {
                    modules: [Navigation],
                    spaceBetween: 10,
                    slidesPerView: 4.3,
                    freeMode: true,
                    watchSlidesProgress: true,
                    navigation: {
                        nextEl: this.element.parentNode.querySelector(`.swiper-button-next-${that.id}-thumbnail`),
                        prevEl: this.element.parentNode.querySelector(`.swiper-button-prev-${that.id}-thumbnail`),
                    },
                });
                carouselOptions = {
                    breakpoints: {
                        0: {
                            slidesPerView: 1.2,
                        },
                        800: {
                            slidesPerView: 1,
                        },
                    },
                    thumbs: {
                        swiper: thumbSwiper,
                    },
                    ...carouselOptions,
                };
            } else {
                carouselOptions = {
                    slidesPerView: this.multiSlide ? "auto" : 1,
                    breakpoints: {
                        900: this.autoPlay
                            ? {
                                  autoplay: {
                                      enabled: true,
                                      delay: this.autoPlayDuration ? Number(this.autoPlayDuration) : 5000,
                                      pauseOnMouseEnter: true,
                                      disableOnInteraction: true,
                                  },
                              }
                            : {},
                    },
                    ...carouselOptions,
                };
            }

            that.carousel = new Swiper(that.element, {
                modules: [Navigation, Pagination, Autoplay, Thumbs],
                pagination: {
                    el: `.swiper-pagination-${that.id}`,
                    clickable: true,
                    renderBullet: (index, className) => {
                        const label = `Go to slide ${index + 1}`;

                        return `<button title="${label}" aria-label="${label}" class="${className}"></button>`;
                    },
                },
                navigation: {
                    nextEl: this.element.getElementsByClassName(`swiper-button-next-${that.id}`)[0],
                    prevEl: this.element.getElementsByClassName(`swiper-button-prev-${that.id}`)[0],
                },
                ...carouselOptions,
            });
        });
    }

    /**
     * Destroy carousel.
     *
     * @return {void}
     */
    destroy() {
        this.carousel.destroy();
    }
}

export default {
    init: (element) => {
        instances.push(new Carousel(element));
    },

    destroy: () => {
        instances.forEach((instance) => instance.destroy());
        instances = [];
    },
};
